import type { BobRunReport } from "../../api/bobReports";
import {
  commitRangeHref, daysBetween, fmtDateTime, openAfter, repoName, runState, runSummary, sevCounts, sevMixText, shortSha, usd, walkthrough,
} from "../../lib/bobReports";
import type { ReportNav } from "./reportNav";
import { CatChip, FindingCard, Tile } from "./reportParts";

const KIND_TITLE: Readonly<Record<string, string>> = { push: "Push review", manual: "Manual review" };

function ownList(list: readonly unknown[], total: number, count: number): boolean {
  return list.length === total && total === count;
}

const STATE_CHIP = {
  completed: { label: "Completed", cls: "br-chip-ok" },
  failed: { label: "Did not finish", cls: "br-chip-block" },
  partial: { label: "Partial", cls: "br-chip-flag" },
  displaced: { label: "Superseded by a whole-pack review", cls: "br-chip-cat" },
} as const;

/** Breadcrumb back to the Overview, the console and the runs list. */
export function ReportCrumbs({ nav, here }: { nav: ReportNav; here: string }) {
  return (
    <div className="br-crumbs">
      <button type="button" className="br-linkbtn" onClick={() => nav.openTab("overview")}>All packs</button>
      <span aria-hidden="true">/</span>
      <button type="button" className="br-linkbtn" onClick={() => nav.openTab("ledger")}>{nav.projectName}</button>
      <span aria-hidden="true">/</span>
      <button type="button" className="br-linkbtn" onClick={() => nav.openRun(null)}>Reports</button>
      <span aria-hidden="true">/</span>
      <span>{here}</span>
    </div>
  );
}

export function SameHeadNote({ report, nav }: { report: BobRunReport; nav: ReportNav }) {
  const others = report.same_head_run_ids.filter((id) => id !== report.run.id);
  if (others.length === 0) return null;
  return (
    <p className="br-note" role="note">
      {others.length === 1 ? "Run " : "Runs "}
      {others.map((id, i) => (
        <span key={id}>
          {i > 0 && (i === others.length - 1 ? " and " : ", ")}
          <a href={nav.runHref(id)} onClick={(e) => { e.preventDefault(); nav.openRun(id); }}>#{id}</a>
        </span>
      ))}
      {" "}reviewed the same commit, so the lists of new and fixed findings below are shared by {others.length === 1 ? "both runs" : "all of them"}.
      {" "}The numbers above count this run&apos;s own.
    </p>
  );
}

export default function RunReportView({ report, nav }: { report: BobRunReport; nav: ReportNav }) {
  const { run, findings } = report;
  const state = runState(run);
  const chip = STATE_CHIP[state];
  const newMix = sevCounts(findings.new);
  const fixedMix = sevCounts(findings.fixed);
  const walk = walkthrough(report);
  const after = openAfter(run);
  const cost = nav.isAdmin && typeof run.cost_usd === "number" ? run.cost_usd : null;
  const filesChanged = report.changed_files_count;
  // The lists hold every finding credited to this commit, so their mix and
  // their still-open count describe this run only when they are its own.
  const ownNew = ownList(findings.new, findings.new_total, run.new_count);
  const newNote = ownNew && run.new_count > 0
    ? [sevMixText(newMix), `${findings.still_open_count} still open`].filter((s) => s !== "").join(", ")
    : ownNew ? "none" : undefined;

  return (
    <div className="br-page br-narrow">
      <header className="br-head-col">
        <ReportCrumbs nav={nav} here={`Review run ${run.id}`} />
        <div className="br-row">
          <h2 className="br-h1">{KIND_TITLE[run.kind] ?? "Review"}</h2>
          <CatChip size="lg">{repoName(run.repo)} / {run.branch}</CatChip>
          <span className={`br-chip br-lg ${chip.cls}`}>{chip.label}</span>
        </div>
        <div className="br-meta">
          <span>{fmtDateTime(run.created_at)}</span>
          <a className="br-mono" href={commitRangeHref(run.repo, run.base_sha, run.head_sha)} target="_blank" rel="noreferrer">
            {run.base_sha ? `${shortSha(run.base_sha)}...${shortSha(run.head_sha)}` : shortSha(run.head_sha)}
          </a>
          {filesChanged !== null && <span>{filesChanged} {filesChanged === 1 ? "file" : "files"} changed</span>}
        </div>
      </header>

      {state === "failed" && run.error && (
        <div className="br-banner" role="alert">
          <div className="br-banner-head"><strong>This review did not finish</strong></div>
          <p>{run.error}</p>
        </div>
      )}

      <section className="br-panel br-sunken" aria-label="Summary">
        <span className="br-eyebrow">Summary</span>
        <p className="br-lead">{runSummary(report)}</p>
        <SameHeadNote report={report} nav={nav} />
      </section>

      <section aria-label="Numbers" className="br-grid-tiles br-small-tiles">
        <Tile label="New" value={run.new_count} valueColor={run.new_count > 0 ? "var(--flag)" : undefined} note={newNote} />
        <Tile label="Fixed" value={run.fixed_count} valueColor={run.fixed_count > 0 ? "var(--ok)" : undefined}
          note={ownList(findings.fixed, findings.fixed_total, run.fixed_count) ? sevMixText(fixedMix) || "none" : undefined} />
        {after !== null && <Tile label="Still open" value={after} note={`${run.open_at_start} at start`} />}
        {typeof run.checked === "number" && <Tile label="Re-checked" value={run.checked} note="open findings" />}
        <Tile label="Deferred" value={run.deferred} note="to the next push" />
        {run.reopened_count > 0 && <Tile label="Reopened" value={run.reopened_count} note="came back after a fix" />}
        {cost !== null && <Tile label="Cost" value={usd(cost)} />}
      </section>

      {walk.length > 0 && (
        <section className="br-panel" aria-label="Walkthrough">
          <div className="br-row" style={{ justifyContent: "space-between", gap: 8 }}>
            <h2 className="br-h2">Walkthrough</h2>
            <span className="br-small br-muted">Files with a new or fixed finding in this review</span>
          </div>
          <div className="br-table-wrap">
            <table className="br-table">
              <thead><tr><th scope="col">File</th><th scope="col" className="br-r">Findings</th></tr></thead>
              <tbody>
                {walk.map((w) => (
                  <tr key={w.file}>
                    <td className="br-mono" style={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{w.file}</td>
                    <td className="br-r">
                      {w.added > 0 && <span className="br-chip br-chip-flag">{w.added} new</span>}
                      {w.added > 0 && w.fixed > 0 && " "}
                      {w.fixed > 0 && <span className="br-chip br-chip-ok">{w.fixed} fixed</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {findings.new_total > 0 && (
        <section className="br-items" aria-label="New findings">
          <h2 className="br-h2">New findings <span className="br-num">{findings.new_total}</span></h2>
          {findings.new.map((f) => (
            <FindingCard key={f.id} finding={f} href={nav.findingHref(f.id)} onOpen={nav.openFinding}
              meta={f.status === "open" ? undefined : f.status} />
          ))}
          {findings.new.length < findings.new_total && (
            <span className="br-note">Showing {findings.new.length} of {findings.new_total}, worst first.</span>
          )}
        </section>
      )}

      {findings.fixed_total > 0 && (
        <section className="br-items" aria-label="Fixed findings">
          <h2 className="br-h2">Fixed by this {run.kind === "push" ? "push" : "review"} <span className="br-num">{findings.fixed_total}</span></h2>
          {findings.fixed.map((f) => {
            const days = daysBetween(f.created_at, f.updated_at);
            return (
              <FindingCard
                key={f.id}
                finding={f}
                fixed
                href={nav.findingHref(f.id)}
                onOpen={nav.openFinding}
                meta={days === null ? undefined : `open ${days} ${days === 1 ? "day" : "days"}`}
                extra={f.resolved_reason ? <span className="br-why"><b>Why Bob closed it:</b> {f.resolved_reason}</span> : undefined}
              />
            );
          })}
          {findings.fixed.length < findings.fixed_total && (
            <span className="br-note">Showing {findings.fixed.length} of {findings.fixed_total}, worst first.</span>
          )}
        </section>
      )}

      <div className="br-grid-two br-narrow-two">
        <section className="br-panel" aria-label="Deferred">
          <h2 className="br-h2">Deferred</h2>
          <p className="br-p">
            {run.deferred === 0
              ? "Every open finding this push could affect was re-checked."
              : `${run.deferred} open ${run.deferred === 1 ? "finding was" : "findings were"} selected for a re-check and not re-checked against this push. They roll over to the next push to ${run.branch}.`}
          </p>
        </section>
        <section className="br-panel" aria-label="Still open">
          <h2 className="br-h2">Still open on {run.branch}</h2>
          {after !== null && (
            <p className="br-p">
              {after} {after === 1 ? "finding" : "findings"} after this review: {run.open_at_start} carried in, {run.new_count} added,
              {run.reopened_count > 0 ? ` ${run.reopened_count} reopened,` : ""} {run.fixed_count} fixed.
            </p>
          )}
          <button type="button" className="br-btn br-primary" style={{ alignSelf: "flex-start" }} onClick={() => nav.openTab("ledger")}>
            Open the {nav.projectName} console
          </button>
        </section>
      </div>
    </div>
  );
}
